import React from 'react';
import { G, Line, Path, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Series + parallel resistance — two complete circuits side by side with formulas in-line. */
export function DiagramTotalResistance() {
  const zig = (x: number, y: number, label: string, color = DT.stroke) => {
    const pts = Array.from({ length: 9 }).map((_, i) => {
      const px = x + (i * 5);
      const py = y + (i % 2 === 0 ? 0 : (i % 4 === 1 ? -7 : 7));
      return `${px},${py}`;
    });
    return (
      <G>
        <Path d={`M${pts.join(' L')}`} fill="none" stroke={color} strokeWidth="1.8" />
        <SvgText x={x + 22} y={y - 12} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">{label}</SvgText>
      </G>
    );
  };

  return (
    <DiagramCanvas>
      {/* === SERIES (top) === */}
      <SvgText x={175} y={42} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">SERIES</SvgText>
      {/* Loop wires */}
      <Line x1={40} y1={70} x2={300} y2={70} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={40} y1={70} x2={40} y2={110} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={300} y1={70} x2={300} y2={110} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={40} y1={110} x2={300} y2={110} stroke={DT.stroke} strokeWidth="1.6" />
      {/* Battery */}
      <Line x1={28} y1={84} x2={52} y2={84} stroke={DT.stroke} strokeWidth="3" />
      <Line x1={32} y1={94} x2={48} y2={94} stroke={DT.stroke} strokeWidth="2" />
      {/* R1 + R2 + R3 in series on top */}
      {zig(80, 70, 'R₁')}
      {zig(150, 70, 'R₂')}
      {zig(220, 70, 'R₃')}
      {/* Equivalent */}
      <SvgText x={175} y={128} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">R_t = R₁ + R₂ + R₃</SvgText>

      {/* === PARALLEL (bottom) === */}
      <SvgText x={175} y={158} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">PARALLEL</SvgText>
      {/* Bus bars */}
      <Line x1={70} y1={172} x2={280} y2={172} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={70} y1={222} x2={280} y2={222} stroke={DT.stroke} strokeWidth="1.6" />
      {/* Source connection */}
      <Line x1={40} y1={172} x2={70} y2={172} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={40} y1={222} x2={70} y2={222} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={40} y1={172} x2={40} y2={222} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={28} y1={190} x2={52} y2={190} stroke={DT.stroke} strokeWidth="3" />
      <Line x1={32} y1={200} x2={48} y2={200} stroke={DT.stroke} strokeWidth="2" />
      {/* Three branches */}
      {[100, 175, 250].map((bx, i) => (
        <G key={i}>
          <Line x1={bx} y1={172} x2={bx} y2={185} stroke={DT.stroke} strokeWidth="1.6" />
          {zig(bx - 20, 195, `R${i + 1}`)}
          <Line x1={bx} y1={207} x2={bx} y2={222} stroke={DT.stroke} strokeWidth="1.6" />
        </G>
      ))}
      {/* Equation */}
      <Rect x={170} y={232} width={150} height={14} fill="rgba(0,0,0,0.35)" />
      <SvgText x={245} y={243} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">1/R_t = Σ(1/R_i)</SvgText>

      {/* Side legend */}
      <LeaderLine x1={300} y1={90} x2={335} y2={50} varName="R_t" desc="series total" anchor="end" />
      <LeaderLine x1={280} y1={197} x2={335} y2={210} varName="R_t" desc="parallel total" anchor="end" />

      <FormulaTag formula="Series: R_t = ΣR     Parallel: 1/R_t = Σ(1/R)" />
    </DiagramCanvas>
  );
}
